import type { ApplicationNotification, NotificationAction, NotificationDeleteType, NotificationModalInfo } from "./notifications-panel.types";

import { makeAutoObservable } from "mobx";
import type { DropdownScheme, StateProps } from "@synthesis/shared-ui";
import { makeStoreProvider } from "@synthesis/shared-utils";

// ---------------------------------- TYPES ----------------------------------

export type * from "./notifications-panel.types";

// ---------------------------------- CONSTANTS ----------------------------------

export const DELETE_NOTIFICATIONS_DROPDOWN_SCHEME: DropdownScheme = [
  {
    elements: [
      { id: "unread", label: "Удалить непрочитанные" },
      { id: "read", label: "Удалить прочитанные" },
      { id: "all", label: "Удалить все уведомления" },
    ],
    type: "ITEMS",
  },
];

export const createDeleteNotificationsDropdownState = (deleteByType: (type: NotificationDeleteType) => void): StateProps => ({
  all: () => deleteByType("all"),
  read: () => deleteByType("read"),
  unread: () => deleteByType("unread"),
});

export const createNotificationItemDropdownScheme = (isNew: boolean): DropdownScheme => [
  {
    elements: [
      {
        disabled: !isNew,
        id: "markAsRead",
        label: "Отметить прочитанным",
      },
      {
        id: "delete",
        label: "Удалить",
      },
    ],
    type: "ITEMS",
  },
];

export const NOTIFICATIONS_SIDEBAR_OPTIONS = {
  headerHide: true,
  width: "41.875rem",
} as const;

export const NOTIFICATION_LIST_SKELETON_COUNT = 8;
export const NOTIFICATION_ITEM_ESTIMATED_HEIGHT = 176;
export const NOTIFICATIONS_PAGE_SIZE = 20;

// ---------------------------------- UTILS ----------------------------------

const notificationDateFormatter = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatNotificationDate = (value: string) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const time = notificationDateFormatter.format(date).split(", ")[1];

  if (isToday && time) {
    return `Сегодня, ${time}`;
  }

  if (isYesterday && time) {
    return `Вчера, ${time}`;
  }

  return notificationDateFormatter.format(date);
};

export const getApplicationIdFromAction = (action: NotificationAction): string | null => {
  if (action.type !== "link" || typeof action.payload !== "string") {
    return null;
  }

  const match = action.payload.match(/^\/applications\/([^/]+)$/);
  return match?.[1] ?? null;
};

export const getNotificationCreatedAt = (notification: ApplicationNotification): string => {
  if (notification.createdAt) {
    return notification.createdAt;
  }

  if (notification.timestamp) {
    return new Date(notification.timestamp).toISOString();
  }

  return new Date().toISOString();
};

// ---------------------------------- STORE ----------------------------------

class NotificationsStore {
  public modalInfo: NotificationModalInfo | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public openModal(info: NotificationModalInfo) {
    this.modalInfo = info;
  }

  public closeModal() {
    this.modalInfo = null;
  }
}

const { StoreProvider: NotificationsStoreProvider, useStore: useNotificationsStore } = makeStoreProvider(NotificationsStore);

export { NotificationsStoreProvider, useNotificationsStore };
